import { Box } from '@mui/material';
import React, { useEffect } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { IAssistCard } from '../../redux/features/assistRequest/assistRequest.props';
import {
  assistCardDismissed,
  isAssistCardActive,
} from '../../redux/features/assistRequest/assistRequest.slice';
import Colors from '../../utils/color';
import AssistCardHeader, {
  ASSIST_SECTION_SX,
  ASSIST_SIDE_SX,
} from './AssistCardHeader';
import AssistClaimRing from './AssistClaimRing';
import { IAssistBodyProps } from './assistBody.types';
import { getAssistBody } from './assistRequestBodies';

/** The transcript shows the conversation's latest lines, newest last. */
export const TRANSCRIPT_LINES = 4;

interface Props {
  card: IAssistCard;
  onAnswer: IAssistBodyProps['onAnswer'];
}

/**
 * HITL-1773: the request assigned to this agent, whole from the first moment:
 * the agent has a second or two, so there is no claim step. The header (type,
 * store, question; the countdown ring on the right), then, full width and
 * divided into sections, the conversation's last lines and the per-type body
 * (hear_and_confirm: HITL-1783).
 *
 * An ended card is cleared at once, whatever ended it (the answer included):
 * no banner, no Skip.
 */
const AssistRequestCard = ({ card, onAnswer }: Props) => {
  const dispatch = useAppDispatch();
  const active = isAssistCardActive(card);
  const Body = getAssistBody(card.type);
  const transcript = (card.request.context?.transcript ?? []).slice(
    -TRANSCRIPT_LINES
  );

  useEffect(() => {
    if (!active) dispatch(assistCardDismissed());
  }, [active, dispatch]);

  if (!active) return null;

  return (
    <Box
      component="section"
      data-testid="assist-card"
      // Lobby surfaces that collapse on a click elsewhere leave clicks on the
      // assist card alone (IssueBacklogLobbyCard).
      data-assist-card="true"
      data-status={card.status}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        py: 1.75,
        pl: 2,
        pr: 1.5,
        borderRadius: 2,
        backgroundColor: Colors.daintree,
        color: Colors.white,
        borderLeft: `4px solid ${Colors.cerulean}`,
        boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, minWidth: 0, pr: 2, alignSelf: 'flex-start' }}>
          <AssistCardHeader
            type={card.type}
            restaurantCode={card.restaurantCode}
            language={card.request.language}
            question={card.request.question}
          />
        </Box>
        <Box sx={ASSIST_SIDE_SX}>
          <AssistClaimRing
            receivedAt={card.receivedAt}
            deadlineAt={card.deadlineAt}
          />
        </Box>
      </Box>

      {transcript.length > 0 && (
        <Box
          data-testid="assist-transcript"
          sx={{
            ...ASSIST_SECTION_SX,
            display: 'grid',
            gridTemplateColumns: '28px 1fr',
            columnGap: 0.5,
            rowGap: 0.5,
            fontSize: 14,
            lineHeight: 1.35,
          }}
        >
          {transcript.map((line, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              <Box component="span" sx={{ fontWeight: 700, opacity: 0.6 }}>
                {line.who}:
              </Box>
              <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
                {line.text}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      )}

      {Body && <Body card={card} onAnswer={onAnswer} />}
    </Box>
  );
};

export default AssistRequestCard;
